import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  Icon,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Text,
  Textarea,
  Tooltip,
  useLocale,
  useToast,
} from "@app/ui";
import { adminSetOrgBilling, listAllOrgs, setOrgStatus } from "../api/dpp.js";
import { useAuth } from "../auth/AuthContext.js";
import { ErrorAlert } from "../errors/index.js";
import { useT } from "../i18n.js";
import { CreateOrgModal } from "../orgs/CreateOrgModal.js";
import { useOrgs } from "../orgs/OrgContext.js";
import { ShowDemoToggle, useShowDemo } from "../platform/useShowDemo.js";
import type { BillingPlan, OrgStatus, OrgWithCounts } from "../types.js";

const PAGE_SIZE = 15;
const STATUSES: readonly OrgStatus[] = ["active", "suspended", "wound_down"];
const PLANS: readonly BillingPlan[] = ["free", "unlimited", "none"];

/** Attention flags the platform overview links here with (`?flag=`). */
type OrgFlag = "delinquent" | "viewLimit" | "noMembers";
const FLAG_TEST: Record<OrgFlag, (o: OrgWithCounts) => boolean> = {
  delinquent: (o) => o.delinquent,
  viewLimit: (o) => o.viewLimitExceeded,
  noMembers: (o) => o.memberCount === 0,
};

/**
 * Platform-admin console for tenants (org:manage). Lists every organization with
 * its member/product/passport counts, changes lifecycle status, and "steps into"
 * an org — selecting it in OrgContext so the regular product/passport/member
 * pages work on that tenant (the API authorizes this via the org-scope bypass).
 */
export function AdminOrgsPage() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { locale } = useLocale();
  // Date only — the org row is dense; the time adds nothing here.
  const fmtDate = useMemo(() => {
    const f = new Intl.DateTimeFormat(locale, { dateStyle: "medium" });
    return (iso: string) => f.format(new Date(iso));
  }, [locale]);
  const { org: selected, selectOrg, refreshOrgs } = useOrgs();
  const { hasPermission } = useAuth();
  const { showDemo } = useShowDemo();
  // Plan changes go through the billing admin endpoint (system:admin).
  const canBilling = hasPermission("system:admin");
  const [params] = useSearchParams();
  const flagParam = params.get("flag");
  const flag = flagParam && flagParam in FLAG_TEST ? (flagParam as OrgFlag) : null;

  const [orgs, setOrgs] = useState<OrgWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<OrgStatus | "">(
    (STATUSES as readonly string[]).includes(params.get("status") ?? "")
      ? (params.get("status") as OrgStatus)
      : "",
  );
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(params.get("new") === "1");
  const [activeFlag, setActiveFlag] = useState<OrgFlag | null>(flag);

  // Pending lifecycle change, confirmed in a modal (it locks writes for members).
  const [statusTarget, setStatusTarget] = useState<{ org: OrgWithCounts; status: OrgStatus } | null>(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setError(null);
    try {
      setOrgs(await listAllOrgs(showDemo));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => void load(), [showDemo]);

  const nameById = useMemo(() => new Map(orgs.map((o) => [o.id, o.name])), [orgs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase();
    return orgs.filter(
      (o) =>
        (!statusFilter || o.status === statusFilter) &&
        (!activeFlag || FLAG_TEST[activeFlag](o)) &&
        (!q || o.name.toLocaleLowerCase().includes(q) || o.slug.includes(q)),
    );
  }, [orgs, query, statusFilter, activeFlag]);

  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totals = useMemo(
    () =>
      orgs.reduce(
        (acc, o) => ({
          products: acc.products + o.productCount,
          passports: acc.passports + o.passportCount,
          published: acc.published + o.publishedPassportCount,
        }),
        { products: 0, passports: 0, published: 0 },
      ),
    [orgs],
  );

  const enter = (o: OrgWithCounts, path: string) => {
    selectOrg(o.id);
    navigate(path);
  };

  const changePlan = async (o: OrgWithCounts, billingPlan: BillingPlan) => {
    setError(null);
    try {
      // Send the current billing email back: the endpoint treats a missing one as "clear it".
      await adminSetOrgBilling(o.id, { billingPlan, billingEmail: o.billingEmail });
      toast.success(t("adminOrgs.planChanged", { name: o.name }));
      await load();
    } catch (e) {
      setError(e);
    }
  };

  const confirmStatus = async () => {
    if (!statusTarget) return;
    setSaving(true);
    try {
      await setOrgStatus(statusTarget.org.id, statusTarget.status, reason.trim() || undefined);
      toast.success(t("adminOrgs.statusChanged", { name: statusTarget.org.name }));
      setStatusTarget(null);
      setReason("");
      await Promise.all([load(), refreshOrgs(selected?.id)]);
    } catch (e) {
      setError(e);
      setStatusTarget(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size="md" style={{ width: "100%" }}>
      <Card
        title={t("adminOrgs.title")}
        extra={
          <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={() => setCreateOpen(true)}>
            {t("adminOrgs.create")}
          </Button>
        }
      >
        <Text small secondary>
          {t("adminOrgs.summary", {
            orgs: orgs.length,
            products: totals.products,
            passports: totals.passports,
            published: totals.published,
          })}
        </Text>
        {error != null && <ErrorAlert error={error} onRetry={() => void load()} />}
        <div className="flex flex-wrap gap-3" style={{ margin: "var(--az-sp-3) 0" }}>
          <div style={{ width: 320, maxWidth: "100%" }}>
            <Input
              prefix={<Icon name="search" size={14} />}
              placeholder={t("adminOrgs.search")}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div style={{ width: 200, maxWidth: "100%" }}>
            <Select
              options={[
                { label: t("adminOrgs.allStatuses"), value: "" },
                ...STATUSES.map((s) => ({ label: t(`orgStatus.${s}`), value: s })),
              ]}
              value={statusFilter}
              onChange={(v) => {
                setStatusFilter(v as OrgStatus | "");
                setPage(1);
              }}
            />
          </div>
          <ShowDemoToggle />
          {activeFlag && (
            <Button
              size="sm"
              variant="subtle"
              icon={<Icon name="close" size={12} />}
              onClick={() => setActiveFlag(null)}
            >
              {t(`adminOrgs.flag.${activeFlag}`)}
            </Button>
          )}
        </div>
        <Table
          rowKey={(o) => o.id}
          loading={loading}
          empty={t("adminOrgs.empty")}
          columns={[
            {
              key: "name",
              title: t("adminOrgs.name"),
              render: (o) => (
                <div className="min-w-0">
                  <div className="font-medium">{o.name}</div>
                  <Text small secondary>
                    {o.parentId ? `${nameById.get(o.parentId) ?? "—"} › ` : ""}
                    {o.slug} · {fmtDate(o.createdAt)}
                  </Text>
                  {(o.isDemo || o.delinquent || o.viewLimitExceeded || o.memberCount === 0) && (
                    <div className="flex flex-wrap gap-1" style={{ marginTop: 4 }}>
                      {o.isDemo && <Tag color="primary">{t("platform.demoTag")}</Tag>}
                      {o.delinquent && <Tag color="error">{t("adminOrgs.flag.delinquent")}</Tag>}
                      {o.viewLimitExceeded && <Tag color="warning">{t("adminOrgs.flag.viewLimit")}</Tag>}
                      {o.memberCount === 0 && <Tag color="warning">{t("adminOrgs.flag.noMembers")}</Tag>}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: "status",
              title: t("common.status"),
              render: (o) => (
                <div style={{ width: 130 }}>
                  <Select
                    size="sm"
                    options={STATUSES.map((s) => ({ label: t(`adminOrgs.status.${s}`), value: s }))}
                    value={o.status}
                    onChange={(v) => {
                      if (v !== o.status) setStatusTarget({ org: o, status: v as OrgStatus });
                    }}
                  />
                </div>
              ),
              width: 150,
            },
            {
              key: "plan",
              title: t("org.plan"),
              render: (o) =>
                canBilling ? (
                  <div style={{ width: 140 }}>
                    <Select
                      size="sm"
                      options={PLANS.map((p) => ({ label: t(`adminOrgs.plan.${p}`), value: p }))}
                      value={o.billingPlan}
                      onChange={(v) => {
                        if (v !== o.billingPlan) void changePlan(o, v as BillingPlan);
                      }}
                    />
                  </div>
                ) : (
                  <Tag>{t(`adminOrgs.plan.${o.billingPlan}`)}</Tag>
                ),
              hideOnMobile: true,
            },
            { key: "members", title: t("adminOrgs.members"), numeric: true, render: (o) => o.memberCount },
            { key: "products", title: t("nav.products"), numeric: true, render: (o) => o.productCount },
            {
              key: "passports",
              title: t("nav.passports"),
              numeric: true,
              render: (o) => `${o.publishedPassportCount} / ${o.passportCount}`,
            },
            {
              key: "actions",
              title: "",
              render: (o) => (
                // Icon-only so the row fits; each steps into the org on that page.
                <Space size="sm">
                  <Tooltip title={t("nav.products")}>
                    <Button
                      size="sm"
                      variant="subtle"
                      aria-label={t("nav.products")}
                      icon={<Icon name="box" size={14} />}
                      onClick={() => enter(o, "/products")}
                    />
                  </Tooltip>
                  <Tooltip title={t("nav.passports")}>
                    <Button
                      size="sm"
                      variant="subtle"
                      aria-label={t("nav.passports")}
                      icon={<Icon name="file-text" size={14} />}
                      onClick={() => enter(o, "/passports")}
                    />
                  </Tooltip>
                  <Tooltip title={t("adminOrgs.manage")}>
                    <Button
                      size="sm"
                      variant="subtle"
                      aria-label={t("adminOrgs.manage")}
                      icon={<Icon name="settings" size={14} />}
                      onClick={() => enter(o, "/org")}
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
          data={pageRows}
        />
        <div style={{ marginTop: "var(--az-sp-3)" }}>
          <Pagination total={filtered.length} pageSize={PAGE_SIZE} current={page} onChange={setPage} />
        </div>
      </Card>

      <CreateOrgModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          void load();
        }}
      />

      <Modal
        open={statusTarget != null}
        onClose={() => setStatusTarget(null)}
        title={t("adminOrgs.statusConfirmTitle")}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        onOk={() => void confirmStatus()}
        confirmLoading={saving}
      >
        {statusTarget && (
          <Space direction="vertical" size="md" style={{ width: "100%" }}>
            <Text>
              {t("adminOrgs.statusConfirmBody", {
                name: statusTarget.org.name,
                status: t(`orgStatus.${statusTarget.status}`),
              })}
            </Text>
            <Textarea
              label={t("adminOrgs.statusReason")}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </Space>
        )}
      </Modal>
    </Space>
  );
}
